import { useEffect, useRef, useState } from 'react'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string

// Set Google Maps API options once globally (persist across HMR)
const globalWithConfig = globalThis as typeof globalThis & { __GOOGLE_MAPS_CONFIGURED__?: boolean }
if (typeof window !== 'undefined' && GOOGLE_MAPS_API_KEY && !globalWithConfig.__GOOGLE_MAPS_CONFIGURED__) {
  setOptions({
    key: GOOGLE_MAPS_API_KEY,
    v: 'weekly',
  })
  globalWithConfig.__GOOGLE_MAPS_CONFIGURED__ = true
}

interface PlaceResult {
  street: string
  number: string
  postalCode: string
  city: string
  box?: string
}

/**
 * Hook to integrate Google Places Autocomplete for Belgian addresses
 * Uses the new PlaceAutocompleteElement (recommended by Google)
 * 
 * @param onPlaceSelected - Callback when user selects an address
 * @returns containerRef to attach the autocomplete element
 */
export function useGooglePlaces(
  onPlaceSelected: (place: PlaceResult) => void
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const autocompleteRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null)
  const listenerRef = useRef<((event: Event) => void) | null>(null)
  const callbackRef = useRef(onPlaceSelected)
  const [isLoaded, setIsLoaded] = useState(false)
  // Initialize error immediately if API key is missing — no synchronous setState in effect
  const [error, setError] = useState<string | null>(() =>
    GOOGLE_MAPS_API_KEY ? null : 'Google Maps API key not configured'
  )

  // Update callback ref when it changes (without re-initializing)
  useEffect(() => {
    callbackRef.current = onPlaceSelected
  }, [onPlaceSelected])

  useEffect(() => {
    // Skip if already initialized
    if (autocompleteRef.current) return

    if (!GOOGLE_MAPS_API_KEY) {
      console.error('VITE_GOOGLE_API_KEY is not set in .env.local')
      return
    }

    async function initAutocomplete() {
      try {
        // Load the Places library using new functional API
        await importLibrary('places')
        setIsLoaded(true)
        
        if (!containerRef.current) return

        // Clear container before appending (prevent duplicates)
        containerRef.current.innerHTML = ''

        // Create the new PlaceAutocompleteElement with correct props
        const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({
          componentRestrictions: { country: 'be' }, // Restrict to Belgium
          // Note: No includedPrimaryTypes - defaults to geocode results (addresses)
          // The legacy 'address' type is not valid in the new API
        } as google.maps.places.PlaceAutocompleteElementOptions)

        // Set accessibility and placeholder
        placeAutocomplete.setAttribute('placeholder', 'Commencez à taper l\'adresse...')
        placeAutocomplete.setAttribute('aria-label', 'Rechercher une adresse en Belgique')
        
        // Append the autocomplete element
        containerRef.current.appendChild(placeAutocomplete)
        autocompleteRef.current = placeAutocomplete

        // Create event listener
        const handlePlaceSelect = async (event: Event) => {
          // Access placePrediction from event (new API)
          const { placePrediction } = event as Event & { placePrediction?: google.maps.places.PlacePrediction }

          if (!placePrediction) return

          // Convert prediction to Place object
          const place = placePrediction.toPlace()

          // Fetch address components
          await place.fetchFields({
            fields: ['addressComponents', 'formattedAddress']
          })

          const addressComponents = place.addressComponents as google.maps.places.AddressComponent[]

          if (!addressComponents) return

          // Parse address components with comprehensive fallbacks
          const result: PlaceResult = {
            street: '',
            number: '',
            postalCode: '',
            city: '',
            box: undefined,
          }

          addressComponents.forEach((component) => {
            const types = component.types

            // Street name
            if (types.includes('route')) {
              result.street = component.longText || ''
            } 
            // Street number
            else if (types.includes('street_number')) {
              result.number = component.longText || ''
            } 
            // Postal code
            else if (types.includes('postal_code')) {
              result.postalCode = component.longText || ''
            } 
            // City - with fallbacks for Belgian/French addresses
            else if (types.includes('locality')) {
              result.city = component.longText || ''
            } else if (types.includes('postal_town') && !result.city) {
              result.city = component.longText || ''
            } else if (types.includes('administrative_area_level_2') && !result.city) {
              result.city = component.longText || ''
            } else if (types.includes('sublocality_level_1') && !result.city) {
              result.city = component.longText || ''
            }
            // Box/Apartment (Belgian "boîte")
            else if (types.includes('subpremise')) {
              result.box = component.longText || undefined
            }
          })

          // Handle missing street number (rural/POI addresses)
          if (!result.number && result.street) {
            // Street exists but no number - still valid for some addresses
            result.number = '' // Allow empty number
          }

          // Call callback with parsed address data (require at least street, postal, city)
          if (result.street && result.postalCode && result.city) {
            callbackRef.current(result)
          }
        }

        // Store listener ref for cleanup
        listenerRef.current = handlePlaceSelect

        // Listen for place selection using the correct event name
        placeAutocomplete.addEventListener('gmp-select', handlePlaceSelect)
      } catch {
        setError('Failed to load Google Maps')
      }
    }

    initAutocomplete()

    // Proper cleanup: remove listener and element
    return () => {
      if (autocompleteRef.current && listenerRef.current) {
        autocompleteRef.current.removeEventListener('gmp-select', listenerRef.current)
      }
      if (autocompleteRef.current) {
        autocompleteRef.current.remove()
        autocompleteRef.current = null
      }
      listenerRef.current = null
    }
  }, []) // Empty deps - only initialize once

  return { containerRef, isLoaded, error }
}
